/**
 * Elegir la portada de un inmueble desde el listado.
 *
 * POR QUÉ AQUÍ Y NO SOLO EN LA FICHA. La portada es lo único que se ve del inmueble en la
 * tabla, así que el sitio natural para cambiarla es la propia tabla: se ve la que hay, no
 * convence, se pulsa y se cambia. Obligar a entrar en la ficha, buscar la pestaña de fotos y
 * marcar la portada allí es tres pantallas para una decisión de un segundo.
 *
 * LAS FOTOS SE PIDEN AL ABRIR, no con el listado. El listado ya trae la miniatura de la
 * portada, que es lo que necesita para pintarse; traerse las quince fotos de cada uno de los
 * veinticinco inmuebles por si alguien pulsa una sería pagar por adelantado algo que casi
 * nunca se usa.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, queryKeys } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Drawer } from '../../components/overlay/Drawer';
import { EmptyState } from '../../components/EmptyState';
import { SkeletonCard } from '../../components/Skeleton';
import { toast } from '../../components/feedback/Toaster';
import styles from './CoverPickerDrawer.module.css';

interface CoverPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  propertyId: string | null;
  propertyName: string;
}

export function CoverPickerDrawer({
  open,
  onClose,
  propertyId,
  propertyName,
}: CoverPickerDrawerProps) {
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: queryKeys.property(propertyId ?? ''),
    queryFn: () => api.property(propertyId!),
    enabled: open && Boolean(propertyId),
  });

  const photos = detail.data?.photos ?? [];

  const setCover = useMutation({
    mutationFn: (photoId: string) => api.updatePhoto(photoId, { isCover: true }),
    onSuccess: () => {
      /*
       * El listado guarda la miniatura DENTRO de cada fila, así que hay que invalidarlo: si
       * solo se refresca el detalle, la tabla de detrás sigue enseñando la portada vieja.
       */
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
      if (propertyId) void queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
      toast.success('Portada cambiada.');
      onClose();
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError ? error.message : 'No se ha podido cambiar la portada.',
      ),
  });

  return (
    <Drawer open={open} title="Portada del inmueble" subtitle={propertyName} onClose={onClose}>
      {detail.isLoading ? (
        <SkeletonCard />
      ) : photos.length === 0 ? (
        <EmptyState
          inline
          icon="image"
          title="Este inmueble no tiene fotos"
          body="Súbelas desde la pestaña «Fotos» de su ficha y luego vuelve aquí a elegir cuál va de portada."
        />
      ) : (
        <>
          <p className={styles.hint}>
            Pulsa la que quieras como portada. Es la que se ve en el listado de Propiedades.
          </p>
          <div className={styles.grid}>
            {photos.map((photo) => {
              const url = photo.renditions?.medium ?? photo.url;
              const esPortada = photo.isCover;
              return (
                <button
                  key={photo.id}
                  type="button"
                  className={esPortada ? `${styles.tile} ${styles.tileActive}` : styles.tile}
                  style={photo.placeholderColor ? { background: photo.placeholderColor } : undefined}
                  onClick={() => !esPortada && setCover.mutate(photo.id)}
                  disabled={setCover.isPending}
                  // El estado va en la etiqueta, no solo en el borde dorado: quien no ve el
                  // color necesita saber cuál está puesta.
                  aria-pressed={esPortada}
                  aria-label={
                    esPortada
                      ? `${photo.caption ?? 'Foto'} — es la portada`
                      : `Poner ${photo.caption ?? 'esta foto'} de portada`
                  }
                >
                  {url ? (
                    <img src={url} alt="" loading="lazy" decoding="async" className={styles.image} />
                  ) : null}
                  {esPortada && (
                    <span className={styles.badge}>
                      <Icon name="check" size={13} strokeWidth={2.6} />
                      Portada
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>
    </Drawer>
  );
}
